import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { CarteTerritoire, FOND_OPENFREEMAP, type Position } from '@eec/ui';
import { lireLimites } from './CarteEquipements';

export interface IncidentLocalise {
  id: string;
  titre: string;
  reference: string;
  statut: string;
  latitude: number | null;
  longitude: number | null;
}
export function CarteIncidents({
  incidents,
  onPosition,
  missions = false,
}: {
  incidents: IncidentLocalise[];
  onPosition?: (position: Position) => void;
  missions?: boolean;
}) {
  const [echelle, setEchelle] = useState('gabon');
  const [selection, setSelection] = useState<string | null>(null);
  const [fond, setFond] = useState(true);
  const [recadrage, setRecadrage] = useState(0);
  const limites = useQuery({
    queryKey: ['limites-cartographiques-gabon'],
    queryFn: ({ signal }) => lireLimites(signal),
    staleTime: Infinity,
  });
  const places = incidents.filter(
    (i) =>
      typeof i.latitude === 'number' &&
      Number.isFinite(i.latitude) &&
      typeof i.longitude === 'number' &&
      Number.isFinite(i.longitude),
  );
  const actif = places.find((i) => i.id === selection) ?? (places.length === 1 ? places[0] : null);
  return (
    <section aria-label={missions ? 'Positions GPS des missions' : 'Carte des incidents'}>
      <h2>
        {onPosition
          ? 'Placer la panne sur la carte'
          : missions
            ? 'Pannes de mes missions sur la carte'
            : 'Carte des incidents'}
      </h2>
      <p>
        Fond de rues réel OpenStreetMap / OpenFreeMap. Les noms de rues et quartiers dépendent des
        données disponibles. Les incidents de démonstration restent fictifs.
      </p>
      {onPosition ? (
        <p>
          Zoomez puis cliquez sur le lieu de la panne, ou renseignez ses coordonnées ci-dessous.
        </p>
      ) : (
        <p>
          {places.length} incident(s) géolocalisé(s) ; {incidents.length - places.length} sans
          coordonnées, conservé(s) dans la liste sans point inventé.
        </p>
      )}
      <label>
        <input type="checkbox" checked={fond} onChange={(e) => setFond(e.target.checked)} />{' '}
        Afficher les rues et quartiers (connexion externe)
      </label>
      {missions && <p>Vue centrée sur les positions de vos missions autorisées. Ce cadrage ne constitue pas une limite administrative de juridiction.</p>}
      {limites.isPending && <p role="status">Chargement de la carte…</p>}
      {limites.error && (
        <p role="alert">Carte indisponible. Les coordonnées et la liste restent utilisables.</p>
      )}
      {missions && places.length > 0 && <button type="button" onClick={() => { setSelection(null); setRecadrage(n => n + 1); }}>Recentrer sur mes missions</button>}
      {missions && places.length === 0 && <p>Aucune mission géolocalisée à afficher pour le moment.</p>}
      {limites.data && (!missions || places.length > 0) && (
        <CarteTerritoire
          key={recadrage}
          titre="Localisation des incidents"
          cadrerMarqueurs={missions}
          echelles={limites.data}
          echelle={echelle}
          onEchelle={(id) => {
            setSelection(null);
            setEchelle(id);
          }}
          hauteur={440}
          fond={fond ? FOND_OPENFREEMAP : null}
          onSaisie={onPosition}
          positionCible={actif ? [actif.longitude!, actif.latitude!] : null}
          marqueurActif={actif?.id}
          onMarqueur={setSelection}
          marqueurs={places.map((i, n) => ({
            id: i.id,
            numero: n + 1,
            longitude: i.longitude!,
            latitude: i.latitude!,
            libelle: i.titre,
            ton: ['CLOSED', 'RESTORED_VERIFIED'].includes(i.statut) ? 'autre' : 'ouvert',
          }))}
          libelleObjets={{ singulier: 'incident localisé', pluriel: 'incidents localisés' }}
          libellesTon={{ ouvert: 'Incident en cours', autre: 'Incident rétabli ou clôturé' }}
        />
      )}
      {!onPosition && places.length > 0 && (
        <ul aria-label="Incidents localisés">
          {places.map((i) => (
            <li key={i.id}>
              <button type="button" onClick={() => setSelection(i.id)}>
                Centrer sur {i.titre}
              </button>{' '}
              {!missions && <Link to={`/incidents/${i.id}`}>Ouvrir {i.reference}</Link>}{' '}
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${i.latitude},${i.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Itinéraire vers {i.reference} (Google Maps)
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
